// Armory tiles (PROMPT 3.0 §1) — reads categories from the SSOT
// /lib/catalog/categories.js and fetches per-category hero image + SKU count
// from /api/catalog/category/<id>. Click target: /p/<id> where <id> ∈
// {keyboards, mice, mousepads, keycaps}.

import { useEffect, useState } from "react";
import { HOME } from "../constants/testIds";
import { listCategories } from "../lib/catalog/categories";
import Reveal from "../components/loadout/Reveal";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

// Portrait product shots (mice) get cropped badly by object-cover in the
// 16:10 tile — render them contained instead. Minimal padding so the
// mouse reads large inside the frame.
const TILE_FIT = { mice: "object-contain p-2 md:p-3" };

function useCategoryHero(cats) {
        const [data, setData] = useState({}); // { [id]: { count, featured } }
        useEffect(() => {
                let cancelled = false;
                (async () => {
                        const results = await Promise.all(
                                cats.map(async (c) => {
                                        try {
                                                const r = await fetch(`${BACKEND}/api/catalog/category/${c.id}`);
                                                if (!r.ok) return [c.id, null];
                                                const j = await r.json();
                                                return [c.id, j];
                                        } catch (_e) {
                                                return [c.id, null];
                                        }
                                })
                        );
                        if (cancelled) return;
                        const next = {};
                        for (const [id, val] of results) next[id] = val;
                        setData(next);
                })();
                return () => {
                        cancelled = true;
                };
        }, [cats]);
        return data;
}

export default function CategoryTiles() {
        const cats = listCategories();
        const heroMap = useCategoryHero(cats);

        return (
                <section
                        data-testid={HOME.armorySection}
                        className="bg-white pt-4 pb-14 md:pt-8 md:pb-24"
                >
                        <div className="mx-auto max-w-[1440px] px-6">
                                <Reveal>
                                        <div className="font-mono text-[11px] uppercase tracking-[0.16em] text-[#8A8A8A] mb-4">
                                                02 / ARMORY
                                        </div>
                                </Reveal>
                                <Reveal delay={60}>
                                        <h2 className="font-display text-[#0A0A0A] text-[36px] md:text-[56px] leading-[0.95]">
                                                The Armory.
                                        </h2>
                                </Reveal>
                                <Reveal delay={120}>
                                        <p className="mt-4 font-sans text-[16px] md:text-[18px] text-[#8A8A8A] max-w-[560px] leading-relaxed">
                                                Engineered for the Indian esports operator. Choose your terrain.
                                        </p>
                                </Reveal>

                                <div className="mt-10 md:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                                        {cats.map((c, i) => {
                                                const hero = heroMap[c.id];
                                                const img = hero && hero.featured && hero.featured.image;
                                                const count = hero && typeof hero.count === "number" ? hero.count : null;
                                                return (
                                                        <Reveal key={c.id} delay={60 * i}>
                                                                <a
                                                                        data-testid={HOME.armoryTile}
                                                                        href={`/p/${c.id}`}
                                                                        aria-label={`${c.title} — ${c.subline}`}
                                                                        className="armory-tile group block transition-all"
                                                                >
                                                                        <div className="relative overflow-hidden bg-white" style={{ aspectRatio: "16 / 10" }}>
                                                                                {img ? (
                                                                                        <img
                                                                                                src={img}
                                                                                                alt={(hero.featured && hero.featured.alt) || c.title}
                                                                                                loading={i === 0 ? "eager" : "lazy"}
                                                                                                fetchpriority={i === 0 ? "high" : "auto"}
                                                                                                className={"w-full h-full transition-transform duration-500 group-hover:scale-[1.04] " + (TILE_FIT[c.id] || "object-cover")}
                                                                                                draggable={false}
                                                                                        />
                                                                                ) : (
                                                                                        <div className="w-full h-full bg-[#F5F5F5]" />
                                                                                )}
                                                                        </div>
                                                                        <div className="mt-3 flex items-baseline justify-between gap-3">
                                                                                <h3 className="font-display text-[22px] md:text-[26px] leading-none text-[#0A0A0A]">
                                                                                        {c.title}
                                                                                </h3>
                                                                                <span
                                                                                        data-testid={`armory-tile-count-${c.id}`}
                                                                                        className="font-mono text-[11px] uppercase tracking-[0.04em] text-[#8A8A8A] border border-[#8A8A8A]/40 px-2 py-0.5"
                                                                                >
                                                                                        {count != null ? `${count} SKUs` : "—"}
                                                                                </span>
                                                                        </div>
                                                                        <p className="mt-1.5 font-sans text-[13px] text-[#8A8A8A] leading-snug">
                                                                                {c.subline}
                                                                        </p>
                                                                </a>
                                                        </Reveal>
                                                );
                                        })}
                                </div>
                        </div>
                        <style>{`
                                .armory-tile { transition: transform 200ms ease, border-color 200ms ease; }
                                @media (hover: hover) {
                                        .armory-tile:hover { transform: translateY(-2px); border-color: #FF9F1C; }
                                }
                                .armory-tile:focus-visible {
                                        outline: 2px solid #FF9F1C; outline-offset: 2px;
                                }
                        `}</style>
                </section>
        );
}
